/**
 * DAMAGE & QUARANTINE — THE OPERATIONS WORKBENCH (Slice 4, G-04/G-05).
 *
 * ONE place where a case is settled, and links to it from everywhere a person meets one.
 * The alternative — a small decision form on the challan, another on the passport, another
 * on the customer — was struck down, and rightly: six commands with their own permissions
 * and a money consequence each cannot be scattered across five pages and still be auditable.
 *
 * TWO QUEUES, DELIBERATELY NOT ONE LIST. Damage is about money; quarantine is about safety.
 * They arrive from the same event — a cylinder came back — and they are never merged,
 * because the owner's rule is that a money action and a safety action must not look alike.
 * The styling follows it: money decisions are `kind="primary"`, the safety release is
 * `kind="danger"`, and nothing is preselected anywhere.
 *
 * WHAT THIS FILE DOES NOT DO, and it is the whole discipline of it:
 *   - it does not decide which action is allowed. `nextActions` comes from `DAMAGE_NEXT`
 *     in src/custody/damage.ts, the table the COMMANDS consult, so a button that appears
 *     and a command that accepts are one rule. Hiding a button is never the enforcement;
 *     every command refuses in its own words, and those words are shown.
 *   - it does not compute money. Every figure is read back as posted. The one subtraction
 *     on the page — "the credit this reduction will raise" — is shown as arithmetic the
 *     person can check BEFORE confirming, and after confirming the figure displayed is the
 *     credit line the command actually raised, not this sum.
 *   - it does not add tax. GST is applied by the charge path (D99b) and there is no
 *     authoritative preview endpoint, so the form says the figure is before tax rather
 *     than inventing a total that might not match the one posted.
 *   - it does not judge safety. `blockers` and `otherBlockersRemain` come from
 *     `safetyReasonsFor`, the one place that answers it.
 *
 * AND THE WORDING RULE THE OWNER SET, which is the point of the quarantine half: lifting a
 * quarantine removes THAT hold. It is never "this cylinder is now safe to fill". Where
 * anything else still blocks the vessel the screen says so, by name, after the release.
 */
import * as React from 'react';
import { useState } from 'react';
import { api, can, type Session, type Views } from '../api.js';
import { useAsk, useRun, useRouteQuery, go } from '../hooks.js';
import { Banner, Button, Card, EmptyState, Loading, Money, Pill, Register, Select, Text, TextArea, type Column } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { FleetTabs } from '../fleet.js';
import { date, days, money, rupeesToPaise, today, when, words } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('casework')!.messages;

type Queue = 'damage' | 'quarantine' | 'yards';
type DamageRow = Views['damage.list']['result']['rows'][number];
type DamageDetail = Views['damage.get']['result'];
type QuarantineRow = Views['quarantine.list']['result']['rows'][number];
type Decision = 'assess' | 'dispute' | 'uphold' | 'reduce' | 'waive' | 'void';

/** The queue in the URL, so a link to a case lands on the queue that holds it (DEF-253). */
export function asQueue(raw: string | null): Queue {
  return raw === 'quarantine' ? 'quarantine' : raw === 'yards' ? 'yards' : 'damage';
}

export function Casework({ session }: { session: Session }) {
  // THE ADDRESS IS THE STATE, not a copy of it taken at mount. A link into this screen from
  // the passport, the customer or the challan changes only the query, so a screen that read
  // the query once would stay on whatever it was already showing.
  const queue = asQueue(useRouteQuery('queue'));
  const open = (q: Queue) => {
    // The URL carries the queue AND the open case, so this page can be linked to from the
    // challan, the passport, the customer and the statement — which is the whole design.
    go(`/casework?queue=${q}`);
  };
  return (
    <Page title="Damage & quarantine" sub="What a return threw up, and who has to settle it.">
      <FleetTabs active="casework" session={session} />
      <div className="segmented">
        <button type="button" className={queue === 'damage' ? 'on' : ''} onClick={() => open('damage')}>Damage findings</button>
        <button type="button" className={queue === 'quarantine' ? 'on' : ''} onClick={() => open('quarantine')}>Quarantined cylinders</button>
        <button type="button" className={queue === 'yards' ? 'on' : ''} onClick={() => open('yards')}>Yards</button>
      </div>
      {queue === 'damage' ? <><DamagedCylinders session={session} /><DamageQueue session={session} /></>
        : queue === 'quarantine' ? <QuarantineQueue session={session} />
        : <YardQueue session={session} />}
    </Page>
  );
}

/* ===================================================== the damage queue */

const DAMAGE_COLUMNS: readonly Column<DamageRow>[] = [
  { key: 'age', head: 'Waiting', cell: (r) => days(r.ageDays) },
  { key: 'cyl', head: 'Cylinder', cell: (r) => <><span className="serial">{r.internalId}</span> <span className="muted">{r.serialNo}</span></> },
  { key: 'customer', head: 'Customer', cell: (r) => r.customerName },
  { key: 'what', head: 'What is wrong', cell: (r) => words(r.damageType) + (r.severity ? ` · ${words(r.severity)}` : '') },
  { key: 'challan', head: 'Came back on', cell: (r) => <>{r.challanNumber ?? '—'}{' '}
      {r.branchAssigned
        ? <span className="muted">{r.branch}</span>
        : <Pill tone="testing">unassigned branch</Pill>}</> },
  { key: 'amount', head: 'Assessed', right: true, cell: (r) => (r.assessedAmountPaise === null ? <span className="muted">not priced</span> : <Money paise={r.assessedAmountPaise} />) },
  { key: 'status', head: 'Where it stands', cell: (r) => <Pill tone={r.open ? 'primary' : 'muted'}>{words(r.status)}</Pill> },
  { key: 'next', head: 'What is needed', cell: (r) => nextWords(r) },
];

/**
 * THE REQUIRED NEXT ACTION, IN PLAIN LANGUAGE — not a status word repeated in a second
 * column. A queue that says "disputed" twice has told a person nothing about what to do.
 */
export function nextWords(r: DamageRow): string {
  if (r.nextActions.length === 0) return 'settled — nothing further';
  if (r.nextActions.includes('assess')) return 'needs a price';
  if (r.nextActions.includes('dispute')) return 'charged — the customer may still dispute it';
  if (r.nextActions.includes('uphold')) return 'in dispute — uphold, reduce or waive it';
  return `may be ${r.nextActions.join(' or ')}`;
}


/**
 * EVERY DAMAGED CYLINDER (11 Oct). The charge cases below are the findings a customer may be
 * billed for; this is the full list — came back damaged, found damaged on inspection, or held
 * in quarantine — whether or not a charge was raised, each with where it stands now.
 */
type DamagedRow = Views['damage.cylinders']['result']['rows'][number];
export function DamagedCylinders({ session, base = '/casework?queue=damage' }: { session: Session; base?: string }) {
  const [page, setPage] = useState<number>(1);
  const [q, setQ] = useState<string>('');
  const [show, setShow] = useState<'open' | 'all'>('open');
  const asked = useAsk(() => api.view('damage.cylinders', { page, pageSize: 25, show, ...(q.trim() ? { q: q.trim() } : {}), sort: 'when', dir: 'desc' }), [page, q, show], MESSAGES);
  const cols: readonly Column<DamagedRow>[] = [
    { key: 'cyl', head: 'Cylinder', cell: (r) => <><span className="serial">{r.internalId}</span><div className="muted">{r.serialNo} · {words(r.gasService)}{r.ownerKind !== 'company' ? ' · customer-owned' : ''}</div></> },
    { key: 'finding', head: 'What was found', cell: (r) => <><Pill tone={r.quarantined ? 'danger' : 'money'}>{words(r.finding)}</Pill>{r.quarantined && r.holdReason ? <div className="muted">held: {r.holdReason}</div> : null}</> },
    { key: 'how', head: 'How', cell: (r) => r.foundBy === 'hold' ? 'Put in quarantine' : r.foundBy === 'inspection' ? 'On inspection' : <>Came back damaged{r.challanNumber ? <div className="muted">{r.challanNumber}</div> : null}</> },
    { key: 'who', head: 'From', cell: (r) => r.customerName ?? <span className="muted">—</span> },
    { key: 'when', head: 'When', cell: (r) => date(r.foundOn) },
    { key: 'now', head: 'Now', cell: (r) => r.quarantined ? 'Quarantined' : words(r.state ?? '') },
    { key: 'charge', head: 'Charge', cell: (r) => r.damageId
        ? <a href={`#${base}&case=${r.damageId}`} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`${base}&case=${r.damageId}`); }}>{words(r.damageStatus ?? 'case')} →</a>
        : <span className="muted">none raised</span> },
  ];
  return (
    <Card title="Damaged cylinders" sub="Every cylinder that came back damaged, was found damaged on inspection, or is held in quarantine — whether or not a charge was raised. Newest first.">
      <div className="filters">
        <Text name="dq" label="Cylinder" value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="CTS number or serial" />
        <Select name="dshow" label="Show" value={show} onChange={(v) => { setShow(v as 'open' | 'all'); setPage(1); }}
                options={[{ value: 'open', label: 'Standing here or held' }, { value: 'all', label: 'Everything ever found damaged' }]} />
      </div>
      {asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail !== undefined ? { detail: asked.error.detail } : {})} /> : null}
      {asked.loading && !asked.data ? <Loading what="the damaged cylinders" /> : null}
      {!asked.error && asked.data ? (
        <>
          <Register<DamagedRow> columns={cols} rows={asked.data.rows} onOpen={(r) => go(`#/cylinders/${r.cylinderId}`)}
                    empty={show === 'open' ? 'No cylinder standing here is recorded damaged, and none is held.' : 'No cylinder has ever been recorded damaged.'}
                    totals={<span>Showing <b>{asked.data.rows.length}</b> of <b>{asked.data.page.total}</b></span>} />
          <Pager page={asked.data.page} onPage={setPage} />
        </>
      ) : null}
    </Card>
  );
}

export function DamageQueue({ session, base = '/casework?queue=damage' }: { session: Session; base?: string }) {
  const [page, setPage] = useState<number>(1);
  const [q, setQ] = useState<string>('');
  const [resolution, setResolution] = useState<string>('open');
  const [damageType, setDamageType] = useState<string>('');
  const [branch, setBranch] = useState<string>('');
  const raw = useRouteQuery('case');
  const caseId = raw !== null && /^\d+$/.test(raw) ? Number(raw) : null;

  const asked = useAsk(() => api.view('damage.list', {
    page, pageSize: 25,
    ...(q.trim() ? { q: q.trim() } : {}),
    ...(resolution ? { resolution: resolution as 'open' | 'resolved' } : {}),
    ...(damageType ? { damageType: damageType as 'valve_damaged' | 'body_damage' | 'fire_exposure'
      | 'unknown_contents' | 'other_safety_defect' | 'cosmetic_damage' } : {}),
    ...(branch ? { branch } : {}),
    // OLDEST FIRST. `age` sorts on the date the finding was raised, so the oldest is the
    // EARLIEST date — ascending. Asking for 'desc' here would put today's finding on top
    // and bury the one that has been waiting a fortnight.
    sort: 'age', dir: 'asc',
  }), [page, q, resolution, damageType, branch], MESSAGES);

  const openCase = (id: number | null) => {
    go(id === null ? base : `${base}&case=${id}`);
  };

  if (caseId !== null) return <DamageCase damageId={caseId} session={session} onClose={() => openCase(null)} />;

  return (
    <>
      <Card title="Charge cases" sub="Oldest unsettled first. A case is raised when a cylinder comes back damaged through the customer's fault and money may follow.">
        <div className="filters">
          <Text name="q" label="Cylinder or challan" value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="CTS number, serial or challan" />
          <Select name="resolution" label="Show" value={resolution} onChange={(v) => { setResolution(v); setPage(1); }}
                  options={[{ value: 'open', label: 'Still to settle' }, { value: 'resolved', label: 'Settled' }, { value: '', label: 'Everything' }]} />
          <Select name="damageType" label="Kind" value={damageType} onChange={(v) => { setDamageType(v); setPage(1); }}
                  options={[{ value: '', label: 'Any' },
                            { value: 'valve_damaged', label: 'Valve damaged' },
                            { value: 'body_damage', label: 'Body damage' },
                            { value: 'fire_exposure', label: 'Exposed to fire' },
                            { value: 'unknown_contents', label: 'Contents unknown' },
                            { value: 'other_safety_defect', label: 'Another safety defect' },
                            { value: 'cosmetic_damage', label: 'Cosmetic only' }]} />
          {/* THE CORRECTION WORKLIST. Findings whose return carries no branch — entered
            * before branches were put on challans, or imported from the old register. They
            * are decidable by anybody today, which is the documented fallback and not a
            * licence: this filter is how somebody who knows puts the yard on them. */}
          <Select name="branch" label="Branch" value={branch} onChange={(v) => { setBranch(v); setPage(1); }}
                  options={[{ value: '', label: 'Any' }, { value: 'unassigned', label: 'Unassigned (needs a branch)' }]} />
        </div>
        {/* AN ERROR IS NEVER AN EMPTY QUEUE. A failed read that rendered "nothing to settle"
          * would tell a person their work was done when it had simply not loaded. */}
        {asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail !== undefined ? { detail: asked.error.detail } : {})} {...(asked.error.reference !== undefined ? { reference: asked.error.reference } : {})} /> : null}
        {asked.loading && !asked.data ? <Loading what="the damage findings" /> : null}
        {!asked.error && asked.data ? (
          <>
            <Register columns={DAMAGE_COLUMNS} rows={asked.data.rows} onOpen={(r) => openCase(r.damageId)}
                      empty="No damage findings match. A finding appears here when a return records customer-fault damage." />
            <Pager page={asked.data.page} onPage={setPage} />
          </>
        ) : null}
      </Card>
    </>
  );
}

/* ===================================================== one damage case */

function DamageCase({ damageId, session, onClose }: { damageId: number; session: Session; onClose: () => void }) {
  const asked = useAsk(() => api.view('damage.get', { damageId }), [damageId], MESSAGES);
  const [decision, setDecision] = useState<Decision | null>(null);

  if (asked.error) {
    return (
      <Card title="That case could not be opened" actions={<Button onClick={onClose}>Back to the queue</Button>}>
        <Banner kind="error" title={asked.error.title} {...(asked.error.detail !== undefined ? { detail: asked.error.detail } : {})} {...(asked.error.reference !== undefined ? { reference: asked.error.reference } : {})} />
      </Card>
    );
  }
  if (!asked.data) return <Loading what="the case" />;
  const d = asked.data;

  return (
    <>
      <Card title={`${words(d.damageType)} — ${d.internalId}`}
            sub={`${words(d.status)} · raised ${date(d.history[0]?.at ?? null)}`}
            actions={<Button onClick={onClose}>Back to the queue</Button>}>
        {/* ACTION BEFORE INFORMATION (the owner's standing rule): what may be done, at the
          * top, and nothing preselected. */}
        <Decisions d={d} session={session} decision={decision} onChoose={setDecision}
                   onDone={() => { setDecision(null); asked.reload(); }} />
      </Card>

      <Card title="What this is about" sub="The vessel, the trip it came back on, and who was holding it.">
        <dl className="facts">
          <Fact k="Cylinder">
            <a href={`#/cylinders/${d.cylinderId}`}>{d.internalId}</a>{' '}
            <span className="muted">{d.serialNo} · {d.gasService}{d.cylinderBranch ? ` · lives at ${d.cylinderBranch}` : ''}</span>
          </Fact>
          {/* WHERE THE RETURN WAS HANDLED decides who may settle this — and it is NOT where
            * the vessel lives, which is why both are shown and neither stands in for the other. */}
          <Fact k="Handled at">
            {d.branchAssigned
              ? <span>{d.branch}</span>
              : <><Pill tone="testing">unassigned</Pill>{' '}
                  <span className="muted">this return pre-dates branches on challans, so anybody who may settle damage can settle it</span></>}
          </Fact>
          <Fact k="Registered owner">
            {d.ownerKind === 'customer' && d.ownerCustomerId !== null
              ? <a href={`#/customers/${d.ownerCustomerId}`}>{d.ownerCustomerName ?? 'a customer'}</a>
              : <span>the company</span>}
          </Fact>
          <Fact k="Customer">
            <a href={`#/customers/${d.customerId}`}>{d.customerName}</a>
            {d.siteName ? <span className="muted"> · {d.siteName}</span> : null}
          </Fact>
          <Fact k="Came back on">
            {d.challanNumber
              ? <a href={`#/challans/${d.challanId}`}>{d.challanNumber}</a>
              : <span>challan #{d.challanId}</span>}
            <span className="muted"> · {date(d.challanDate)}</span>
          </Fact>
          <Fact k="Whose fault">{words(d.fault)} — recorded at the return</Fact>
          {d.severity ? <Fact k="Severity">{words(d.severity)}</Fact> : null}
          {d.description ? <Fact k="What was found">{d.description}</Fact> : null}
          {d.observations ? <Fact k="Observations">{d.observations}</Fact> : null}
        </dl>
      </Card>

      <Card title="What it was priced at" sub="And where the figure came from.">
        {d.assessedAmountPaise === null ? (
          <Banner kind="warn" title="No tariff priced this finding"
                  detail="There is no damage tariff rate for this damage type and cylinder specification on the return's date, so nothing has been charged. An authorised assessor must enter the figure, and say why." />
        ) : (
          <dl className="facts">
            <Fact k="Assessed"><Money paise={d.assessedAmountPaise} /></Fact>
            <Fact k="Priced by">
              {d.pricingSource === 'tariff'
                ? <span>the damage tariff{d.tariffEffectiveFrom ? ` in force from ${date(d.tariffEffectiveFrom)}` : ''}{d.tariffEffectiveTo ? ` to ${date(d.tariffEffectiveTo)}` : ''}</span>
                : <span>an authorised assessor</span>}
            </Fact>
            {d.resolvedAmountPaise !== null
              ? <Fact k="Settled at"><Money paise={d.resolvedAmountPaise} /> <span className="muted">after the dispute</span></Fact>
              : null}
          </dl>
        )}
      </Card>

      {/* THE MONEY AS POSTED. Linked, so a person can go and look at the document it sits on
        * rather than taking this page's word for it. */}
      <Card title="The money this raised" sub="Read back as posted. A correction is its own linked instrument — the original is never edited.">
        {d.money.length === 0 ? <EmptyState title="Nothing has been posted" detail="No charge and no credit are attached to this finding." /> : (
          <table className="register">
            <thead><tr><th>What</th><th>Reference</th><th className="right">Amount</th><th>Where it sits</th></tr></thead>
            <tbody>
              {d.money.map((m) => (
                <tr key={`${m.kind}-${m.id}`}>
                  <td><Pill tone={m.kind === 'credit' ? 'good' : 'money'}>{m.kind === 'credit' ? 'Credit' : 'Charge'}</Pill></td>
                  <td>{m.description ?? '—'}</td>
                  <td className="right"><Money paise={m.amountPaise} />{m.gstPaise ? <span className="muted"> + <Money paise={m.gstPaise} /> tax</span> : null}</td>
                  <td>
                    {m.billId !== null
                      ? <a href={`#/billing?bill=${m.billId}`}>{m.billNumber ?? `bill #${m.billId}`}</a>
                      : <span className="muted">{m.effectiveMonth ? `effective ${date(m.effectiveMonth)}` : 'not yet on a bill'}</span>}
                    <span className="muted"> · <a href={`#/account?customer=${d.customerId}`}>the customer's account</a></span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Every decision taken" sub="Who, when, and why. Appended — nothing here is overwritten.">
        <table className="register">
          <thead><tr><th>What happened</th><th>When</th><th>Who</th><th>Why</th><th className="right">Figure</th></tr></thead>
          <tbody>
            {d.history.map((h, i) => (
              <tr key={`${h.what}-${i}`}>
                <td>{words(h.what)}</td>
                <td>{when(h.at)}</td>
                <td>{h.byName ?? '—'}</td>
                <td>{h.reason ?? '—'}</td>
                <td className="right">{h.amountPaise === null ? '' : <Money paise={h.amountPaise} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card title="Evidence" sub="Photographs and inspection notes filed against this finding.">
        {d.attachments.length === 0
          ? <EmptyState title="No photographs are filed" detail="Nothing was attached to this finding when it was raised." />
          : (
            <table className="register">
              <thead><tr><th>File</th><th>Kind</th><th>Dated</th><th>Filed by</th><th /></tr></thead>
              <tbody>
                {d.attachments.map((a) => (
                  <tr key={a.attachmentId}>
                    <td>{a.displayName}</td>
                    <td>{words(a.category)}</td>
                    <td>{date(a.businessOn)}</td>
                    <td>{a.uploadedByName ?? '—'}</td>
                    <td>{a.voided ? <Pill tone="danger">filed in error</Pill> : a.supersededById !== null ? <Pill>superseded</Pill> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </Card>
    </>
  );
}

function Fact({ k, children }: { k: string; children: React.ReactNode }) {
  return <><dt>{k}</dt><dd>{children}</dd></>;
}

/* ===================================================== the six decisions */

/**
 * ONLY WHAT THIS STATE ACCEPTS, and only what this person may do.
 *
 * Two independent filters, and they are not the same question: `d.nextActions` is the
 * LIFECYCLE (does this state accept this decision), `can(session, …)` is AUTHORITY (may
 * this person take it). A case in dispute offers a reader with no permissions the same
 * three decisions a resolver sees — greyed and refused — because the owner asked that an
 * unauthorised person be able to SEE the case and not decide it.
 */
function Decisions({ d, session, decision, onChoose, onDone }: {
  d: DamageDetail; session: Session; decision: Decision | null;
  onChoose: (x: Decision | null) => void; onDone: () => void;
}) {
  if (d.nextActions.length === 0) {
    return <Banner kind="info" title="This case is settled"
                   detail={`It stands as ${words(d.status)}. The money that settled it is linked below and is not edited — a further correction would be its own instrument.`} />;
  }
  if (decision !== null) {
    return <DecisionForm d={d} decision={decision} onCancel={() => onChoose(null)} onDone={onDone} />;
  }
  return (
    <div className="actions">
      {d.nextActions.map((a) => {
        const allowed = can(session, `damage.${a}`);
        return (
          <Button key={a} kind={BUTTON_KIND[a]} disabled={!allowed} onClick={() => onChoose(a)}>
            {LABEL[a]}
          </Button>
        );
      })}
      {d.nextActions.some((a) => !can(session, `damage.${a}`)) ? (
        <p className="muted">
          Some decisions here need a permission you do not hold. You can read the case; a manager grants the rest.
        </p>
      ) : null}
    </div>
  );
}

/**
 * MONEY ACTIONS LOOK DIFFERENT FROM EVERYTHING ELSE (the owner's rule). `primary` is money
 * leaving or returning; `ghost` records a position without moving a figure; `danger` is the
 * one that says the finding should never have existed.
 */
export const BUTTON_KIND: Record<Decision, 'primary' | 'ghost' | 'danger'> = {
  assess: 'primary', reduce: 'primary', waive: 'primary',
  dispute: 'ghost', uphold: 'ghost',
  void: 'danger',
};

export const LABEL: Record<Decision, string> = {
  assess: 'Price this damage',
  dispute: 'The customer disputes it',
  uphold: 'Uphold the charge',
  reduce: 'Reduce it',
  waive: 'Waive it entirely',
  void: 'Entered wholly in error',
};

function DecisionForm({ d, decision, onCancel, onDone }: {
  d: DamageDetail; decision: Decision; onCancel: () => void; onDone: () => void;
}) {
  const runner = useRun(MESSAGES);
  const [reason, setReason] = useState<string>('');
  const [rupees, setRupees] = useState<string>('');
  const [confirming, setConfirming] = useState<boolean>(false);

  const paise = rupeesToPaise(rupees);
  const original = d.assessedAmountPaise ?? 0;
  const needsAmount = decision === 'assess' || decision === 'reduce';
  const ready = reason.trim().length > 0 && (!needsAmount || (paise !== null && paise >= 0));
  // The credit a reduction raises is the DIFFERENCE, and it is shown before confirming so
  // the person sees the consequence of the figure they typed. After the command runs, the
  // page re-reads and shows the credit line that was actually raised.
  const creditPreview = decision === 'reduce' && paise !== null ? original - paise : decision === 'waive' ? original : null;

  const submit = async () => {
    const ok = await runner.run((retryKey) => {
      const key = retryKey !== undefined ? { idempotencyKey: retryKey } : {};
      switch (decision) {
        case 'assess': return api.command('damage.assess', { damageRecordId: d.damageId, amountPaise: paise!, reason: reason.trim() }, key);
        case 'dispute': return api.command('damage.dispute', { damageRecordId: d.damageId, reason: reason.trim() }, key);
        case 'uphold': return api.command('damage.uphold', { damageRecordId: d.damageId, reason: reason.trim() }, key);
        case 'reduce': return api.command('damage.reduce', { damageRecordId: d.damageId, resolvedAmountPaise: paise!, reason: reason.trim() }, key);
        case 'waive': return api.command('damage.waive', { damageRecordId: d.damageId, reason: reason.trim() }, key);
        case 'void': return api.command('damage.void', { damageRecordId: d.damageId, reason: reason.trim() }, key);
      }
    });
    // AUTHORITATIVE REFETCH, always. The screen never patches its own copy of the case from
    // what it hoped the command did.
    if (ok) onDone();
  };

  return (
    <div className="decision">
      <h4>{LABEL[decision]}</h4>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail !== undefined ? { detail: runner.error.detail } : {})} {...(runner.error.reference !== undefined ? { reference: runner.error.reference } : {})} /> : null}

      {decision === 'assess' ? (
        <>
          <Banner kind="warn" title="No tariff priced this finding"
                  detail="You are entering an assessor's own figure. It is recorded as yours, with your reason, and is not a tariff rate." />
          <p className="muted">
            Enter the figure before tax. Tax is added by the charge itself when it is posted — this page does not
            calculate it, so that what you are told and what is charged cannot differ.
          </p>
        </>
      ) : null}

      {decision === 'dispute' ? (
        <p className="muted">
          The charge of <Money paise={original} /> stays exactly as it is. A dispute records that the customer
          objects and who recorded it; it removes nothing.
        </p>
      ) : null}

      {decision === 'uphold' ? (
        <p className="muted">
          <strong>No money changes.</strong> The charge of <Money paise={original} /> stands as posted. This records
          that the dispute was heard and rejected, with your reason.
        </p>
      ) : null}

      {decision === 'reduce' ? (
        <p className="muted">
          Enter what the charge <strong>should have been</strong>, not the amount to credit. The original
          assessment of <Money paise={original} /> is never edited — a credit for the difference is raised against it.
        </p>
      ) : null}

      {decision === 'waive' ? (
        <p className="muted">
          A credit for the whole assessed amount — <Money paise={original} /> — will be raised. The original
          charge stays on the record and on the bill it reached.
        </p>
      ) : null}

      {decision === 'void' ? (
        <>
          <p className="muted">
            This says the finding was <strong>entered wholly in error</strong>. It is not a waiver: a waiver agrees
            the damage happened and forgives the charge, and this says it never should have been recorded.
          </p>
          <p className="muted">
            {d.money.some((m) => m.kind === 'charge') && original > 0
              ? <>A charge of <Money paise={original} /> was posted, so an offsetting credit for the same amount will be raised.</>
              : <>Nothing was charged against this finding, so no credit will be raised.</>}
          </p>
        </>
      ) : null}

      {needsAmount ? (
        <Text name={decision === 'reduce' ? 'resolvedAmountPaise' : 'amountPaise'}
              label={decision === 'reduce' ? 'What it should have been (₹)' : 'What it costs (₹)'}
              value={rupees} onChange={(v) => { setRupees(v); setConfirming(false); }}
              placeholder="0.00" required autoFocus />
      ) : null}

      {creditPreview !== null && creditPreview > 0 ? (
        <p>
          Credit this will raise: <strong><Money paise={creditPreview} /></strong>
          {decision === 'reduce' ? <span className="muted"> — the difference between <Money paise={original} /> and what you entered</span> : null}
        </p>
      ) : null}

      <TextArea name="reason" label={REASON_LABEL[decision]} value={reason}
                onChange={(v) => { setReason(v); setConfirming(false); }}
                help="Required. This is what somebody reads a year from now when they ask why." />

      {/* EVERY ONE OF THESE IS CONFIRMED, because every one of them is either money or a
        * statement about money that cannot be taken back. Nothing is preselected and the
        * confirm step states the consequence in words. */}
      {!confirming ? (
        <div className="actions">
          <Button kind={BUTTON_KIND[decision]} disabled={!ready || runner.busy} onClick={() => setConfirming(true)}>Continue</Button>
          <Button onClick={onCancel}>Cancel</Button>
        </div>
      ) : (
        <div className="actions">
          <Banner kind="warn" title="Confirm this decision" detail={CONSEQUENCE[decision]} />
          <Button kind={BUTTON_KIND[decision]} disabled={runner.busy} onClick={() => { void submit(); }}>
            {runner.busy ? 'Recording…' : `Yes — ${LABEL[decision].toLowerCase()}`}
          </Button>
          <Button onClick={() => setConfirming(false)}>Go back</Button>
        </div>
      )}
    </div>
  );
}

export const REASON_LABEL: Record<Decision, string> = {
  assess: 'Why this figure',
  dispute: 'What the customer says',
  uphold: 'Why the charge stands',
  reduce: 'Why it is reduced',
  waive: 'Why nothing is charged',
  void: 'Why this was entered in error',
};

export const CONSEQUENCE: Record<Decision, string> = {
  assess: 'A charge will be posted to the customer for the figure you entered, plus tax, and it will appear on their account and their next bill.',
  dispute: 'The charge is left exactly as it is. The case moves to "in dispute" and waits for somebody to uphold, reduce or waive it.',
  uphold: 'No money moves. The charge stands as posted and the case is closed.',
  reduce: 'A credit for the difference will be raised against the customer. The original charge is not edited.',
  waive: 'A credit for the whole assessed amount will be raised against the customer. The original charge is not edited.',
  void: 'The finding will be marked as entered wholly in error. Where a charge was posted, an offsetting credit is raised.',
};

/* ================================================= the quarantine queue */

export function QuarantineQueue({ session }: { session: Session }) {
  const [page, setPage] = useState<number>(1);
  const [q, setQ] = useState<string>('');
  const asked = useAsk(() => api.view('quarantine.list', {
    page, pageSize: 25, ...(q.trim() ? { q: q.trim() } : {}), sort: 'age', dir: 'asc',
  }), [page, q], MESSAGES);

  return (
    <Card title="Quarantined cylinders" sub="Longest held first. A quarantine is one hold on one vessel — lifting it is not a finding of fitness.">
      <div className="filters">
        <Text name="q" label="Cylinder" value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="CTS number or serial" />
      </div>
      {asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail !== undefined ? { detail: asked.error.detail } : {})} {...(asked.error.reference !== undefined ? { reference: asked.error.reference } : {})} /> : null}
      {asked.loading && !asked.data ? <Loading what="the quarantined cylinders" /> : null}
      {!asked.error && asked.data ? (
        asked.data.rows.length === 0
          ? <EmptyState title="Nothing is in quarantine" detail="No cylinder in this company has an open quarantine hold." />
          : (
            <>
              <div className="quarantine-list">
                {asked.data.rows.map((r) => (
                  <QuarantineCase key={r.holdId} r={r} session={session} onDone={() => asked.reload()} />
                ))}
              </div>
              <Pager page={asked.data.page} onPage={setPage} />
            </>
          )
      ) : null}
    </Card>
  );
}

export function QuarantineCase({ r, session, onDone }: { r: QuarantineRow; session: Session; onDone: () => void }) {
  const runner = useRun(MESSAGES);
  const [reason, setReason] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [confirming, setConfirming] = useState<boolean>(false);
  const mayRelease = can(session, 'job.release_quarantine');

  /**
   * TWO ROUTES, AND THE VIEW SAYS WHICH (Slice 4A).
   *
   * A vessel on a customer job is released through `job.releaseQuarantine`, which names the
   * job. One on no job at all — a failed quality check isolates a company cylinder — goes
   * through `cylinder.releaseQuarantine`, which names the exact hold so a stale screen
   * cannot clear a different one. The third case is not a route: an ownership dispute's
   * isolation closes with the dispute, and both commands refuse it by name.
   */
  const release = async () => {
    if (r.releaseRoute === 'dispute') return;
    const ok = await runner.run((retryKey) => {
      const key = retryKey !== undefined ? { idempotencyKey: retryKey } : {};
      return r.releaseRoute === 'job'
        ? api.command('job.releaseQuarantine', { jobId: r.jobId!, cylinderId: r.cylinderId, reason: reason.trim() }, key)
        : api.command('cylinder.releaseQuarantine', { cylinderId: r.cylinderId, holdId: r.holdId, reason: reason.trim() }, key);
    });
    if (ok) onDone();
  };

  return (
    <div className="card quarantine-case">
      <h4>
        <a href={`#/cylinders/${r.cylinderId}`}>{r.internalId}</a>{' '}
        <span className="muted">{r.serialNo} · {r.gasService}{r.branch ? ` · ${r.branch}` : ''}</span>
      </h4>
      <dl className="facts">
        <Fact k="Held for">{days(r.ageDays)}</Fact>
        <Fact k="Why">{r.why}</Fact>
        <Fact k="Quarantined by">{r.openedByName ?? '—'} <span className="muted">{when(r.openedAt)}</span></Fact>
        <Fact k="Registered owner">
          {r.ownerKind === 'customer' && r.ownerCustomerId !== null
            ? <a href={`#/customers/${r.ownerCustomerId}`}>{r.ownerCustomerName ?? 'a customer'}</a>
            : <span>the company</span>}
        </Fact>
        {r.jobId !== null ? (
          <Fact k="Came in on">
            <a href={`#/customers/${r.jobCustomerId ?? ''}`}>{r.jobCustomerName ?? 'a customer'}</a>
            <span className="muted"> · job #{r.jobId}{r.jobStatus ? ` · ${words(r.jobStatus)}` : ''}</span>
          </Fact>
        ) : null}
        {r.inspectionOutcome ? (
          <Fact k="Inspection found">{words(r.inspectionOutcome)} <span className="muted">— {r.inspectedByName ?? 'somebody'}, {when(r.inspectedAt)}</span></Fact>
        ) : null}
        {r.openedForDispute ? (
          <Fact k="Opened by">an ownership dispute, not a safety finding</Fact>
        ) : r.jobId === null ? (
          <Fact k="Opened by">a safety finding, on no customer job — most likely a failed quality check</Fact>
        ) : null}
      </dl>

      {r.observations.length > 0 ? (
        <table className="register">
          <thead><tr><th>What was measured</th><th>Reading</th><th>When</th></tr></thead>
          <tbody>
            {r.observations.map((o, i) => (
              <tr key={`${o.kind}-${i}`}>
                <td>{words(o.kind)}</td>
                <td>{o.value}{o.unit ? ` ${o.unit}` : ''}</td>
                <td>{when(o.recordedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <p className="muted">No valve, body or pressure reading is recorded against this vessel.</p>}

      {/* EVERYTHING THAT BLOCKS IT, not just the quarantine. This is the list that makes the
        * owner's wording rule enforceable: if there is more than one line here, lifting the
        * quarantine leaves the vessel blocked, and the page says which by name. */}
      <h5>What stops this cylinder now</h5>
      <ul className="blockers">
        {r.blockers.map((b) => (
          <li key={b.reason}><Pill tone={b.reason === 'quarantined' ? 'testing' : 'danger'}>{words(b.reason)}</Pill> {b.says}</li>
        ))}
      </ul>
      {r.otherBlockersRemain ? (
        <Banner kind="warn" title="Lifting the quarantine will NOT make this cylinder fit"
                detail="Something else blocks it as well, named above. Releasing the quarantine removes that one hold and nothing more — every other safety fact still governs." />
      ) : null}
      <p className="muted">
        Still allowed: {r.stillAllowed.length === 0 ? 'nothing' : r.stillAllowed.map((a) => words(a)).join(', ')}.
      </p>

      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail !== undefined ? { detail: runner.error.detail } : {})} {...(runner.error.reference !== undefined ? { reference: runner.error.reference } : {})} /> : null}

      {r.releaseRoute === 'dispute' ? (
        <Banner kind="warn" title="This isolation is not lifted here"
                detail="This vessel is isolated because its ownership is disputed, not because anything is wrong with it. That isolation closes with the dispute itself and at the same moment — settle the dispute, and both close together. Neither release command will lift it." />
      ) : !mayRelease ? (
        <p className="muted">Lifting a quarantine needs a permission you do not hold. A manager grants it.</p>
      ) : !open ? (
        <div className="actions"><Button kind="danger" onClick={() => setOpen(true)}>Lift the quarantine</Button></div>
      ) : (
        <div className="decision">
          <TextArea name="reason" label="What makes it safe to lift the quarantine" value={reason}
                    onChange={(v) => { setReason(v); setConfirming(false); }}
                    help="Required. Name the evidence — the repair, the re-test, the inspection — not just a decision." />
          {!confirming ? (
            <div className="actions">
              <Button kind="danger" disabled={reason.trim().length === 0 || runner.busy} onClick={() => setConfirming(true)}>Continue</Button>
              <Button onClick={() => { setOpen(false); setReason(''); }}>Cancel</Button>
            </div>
          ) : (
            <div className="actions">
              <Banner kind="warn" title="Confirm you are lifting this quarantine"
                      detail={r.otherBlockersRemain
                        ? 'This removes the quarantine hold only. The cylinder stays blocked by what is named above, and will not become fillable.'
                        : 'This removes the quarantine hold and records the safety decision in your name, with your reason.'} />
              <Button kind="danger" disabled={runner.busy} onClick={() => { void release(); }}>
                {runner.busy ? 'Recording…' : 'Yes — lift the quarantine'}
              </Button>
              <Button onClick={() => setConfirming(false)}>Go back</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ===================================================== where each vessel is standing (Slice 4B) */

type YardRow = Views['cylinders.yards']['result']['rows'][number];

/**
 * THE YARD LIST, AND THE EXCEPTION QUEUE INSIDE IT.
 *
 * Two different things on one screen, deliberately: a branch's own standing stock, and the
 * vessels whose yard was never recorded. The second is not ordinary work — it is a
 * correction, and it is drawn as one. 0128 left those rows NULL rather than copying the
 * cylinder's home branch, because where a vessel is owned is not where it is standing, and
 * a wrong yard is worse than no yard: no yard refuses a branch-scoped decision, a wrong one
 * hands it to the wrong person.
 */
export function YardQueue({ session }: { session: Session }) {
  const [page, setPage] = useState<number>(1);
  const [q, setQ] = useState<string>('');
  const [state, setState] = useState<string>('all');
  const [branch, setBranch] = useState<string>('');
  const asked = useAsk(() => api.view('cylinders.yards', {
    page, pageSize: 25,
    ...(q.trim() ? { q: q.trim() } : {}),
    ...(state === 'unknown' ? { state: 'unknown' as const }
        : state === 'in_transit' ? { state: 'in_transit' as const } : {}),
    ...(branch.trim() ? { branch: branch.trim() } : {}),
  }), [page, q, state, branch], MESSAGES);

  return (
    <Card title="Cylinders standing in our yards"
          sub="Where each vessel actually is — which is not the same question as which branch owns it.">
      <div className="filters">
        <Text name="yq" label="Cylinder" value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="CTS number or serial" />
        <Text name="ybranch" label="Yard" value={branch} onChange={(v) => { setBranch(v); setPage(1); }} placeholder="branch code" />
        <Select name="ystate" label="Show" value={state} onChange={(v) => { setState(v); setPage(1); }}
                options={[{ value: 'all', label: 'All standing stock' },
                          { value: 'in_transit', label: 'On a lorry, between yards' },
                          { value: 'unknown', label: 'Yard not recorded (needs settling)' }]} />
      </div>
      {asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail !== undefined ? { detail: asked.error.detail } : {})} {...(asked.error.reference !== undefined ? { reference: asked.error.reference } : {})} /> : null}
      {asked.loading && !asked.data ? <Loading what="the yards" /> : null}
      {!asked.error && asked.data ? (
        <>
          <Register columns={[...YARD_COLUMNS, {
                      key: 'do', head: 'What can be done', cell: (r: YardRow) =>
                        <YardActions r={r} session={session} onDone={() => asked.reload()} />,
                    }]} rows={asked.data.rows}
                    empty={state === 'unknown'
                      ? 'Every vessel standing in a yard has one recorded. Nothing to settle.'
                      : 'No cylinder is standing in one of our yards.'} />
          <Pager page={asked.data.page} onPage={setPage} />
          {asked.data.rows.some((r) => r.yardUnknown) ? (
            <Banner kind="warn" title="Some of these have no yard recorded"
                    detail="They came from before yards were kept on positions, and they were deliberately NOT assumed to be at the branch that owns them. Until the yard is settled in writing, a branch-scoped decision for them is refused — which is the point." />
          ) : null}
          {session.functions.includes('cylinder.resolve_yard') ? null : (
            <p className="muted">Settling a yard needs a permission you do not hold. A manager grants it.</p>
          )}
        </>
      ) : null}
    </Card>
  );
}

/**
 * MOVING A VESSEL TO ANOTHER YARD, AND SETTLING ONE THAT WAS NEVER RECORDED.
 *
 * Three acts, drawn as what they are. Sending and receiving are the two halves of a
 * transfer and they leave history; settling an unknown yard is a CORRECTION and is drawn
 * apart from them, because a vessel whose yard was never written down is not the same thing
 * as one that has moved.
 */
function YardActions({ r, session, onDone }: { r: YardRow; session: Session; onDone: () => void }) {
  const runner = useRun(MESSAGES);
  const [open, setOpen] = useState<'' | 'send' | 'settle'>('');
  const [branch, setBranch] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const mayMove = can(session, 'cylinder.send_to_branch');
  const maySettle = can(session, 'cylinder.resolve_yard');

  const run = async (what: 'send' | 'settle') => {
    const id = Number(branch);
    if (!Number.isFinite(id) || id <= 0) return;
    const ok = await runner.run((retryKey) => {
      const key = retryKey !== undefined ? { idempotencyKey: retryKey } : {};
      return what === 'send'
        ? api.command('cylinder.sendToBranch',
            { cylinderId: r.cylinderId, toBranchId: id, sentOn: today(), reason: reason.trim() }, key)
        : api.command('cylinder.resolveYard',
            { cylinderId: r.cylinderId, yardBranchId: id, reason: reason.trim() }, key);
    });
    if (ok) { setOpen(''); setBranch(''); setReason(''); onDone(); }
  };

  // A VESSEL ON A LORRY is received, not sent: the yard at the far end confirms it.
  if (r.physicalPosition === 'in_transit') {
    return <ReceiveAtYard r={r} session={session} onDone={onDone} />;
  }
  if (open === '') {
    return (
      <>
        {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail !== undefined ? { detail: runner.error.detail } : {})} /> : null}
        <div className="actions">
          {r.yardUnknown
            ? <Button small kind="primary" disabled={!maySettle} onClick={() => setOpen('settle')}>Say which yard</Button>
            : <Button small disabled={!mayMove} onClick={() => setOpen('send')}>Send to another yard</Button>}
        </div>
      </>
    );
  }
  return (
    <div className="decision">
      <h5>{open === 'send' ? 'Send to another yard' : 'Which yard is it standing in?'}</h5>
      {open === 'send'
        ? <p className="muted">It goes on a lorry and stands in <strong>no yard</strong> until the receiving
            yard confirms it has arrived. Both ends are kept in the movement book.</p>
        : <p className="muted">This vessel was recorded before yards were kept on positions, and it was
            deliberately <strong>not</strong> assumed to be at the branch that owns it. Say where it actually
            is, and how that is known.</p>}
      <Text name="to-branch" label="Yard (its id)" value={branch} onChange={setBranch} required />
      <TextArea name="yard-reason"
                label={open === 'send' ? 'Why it is being moved' : 'How the yard is known'}
                value={reason} onChange={setReason}
                help="Required. Somebody reads this later to judge the decision." />
      <div className="actions">
        <Button kind="primary" disabled={runner.busy || reason.trim().length === 0 || branch.trim().length === 0}
                onClick={() => { void run(open); }}>
          {runner.busy ? 'Recording…' : open === 'send' ? 'Send it' : 'Record the yard'}
        </Button>
        <Button onClick={() => { setOpen(''); setReason(''); setBranch(''); }}>Cancel</Button>
      </div>
    </div>
  );
}

/** A vessel on a lorry between yards: the receiving yard confirms it has arrived. */
function ReceiveAtYard({ r, session, onDone }: { r: YardRow; session: Session; onDone: () => void }) {
  const runner = useRun(MESSAGES);
  const [branch, setBranch] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  if (!can(session, 'cylinder.receive_at_branch')) {
    return <p className="muted">Confirming an arrival needs a permission you do not hold.</p>;
  }
  return (
    <div className="decision">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail !== undefined ? { detail: runner.error.detail } : {})} /> : null}
      <Text name="at-branch" label="This yard (its id)" value={branch} onChange={setBranch} required />
      <TextArea name="receive-reason" label="What was checked" value={reason} onChange={setReason}
                help="Required. The yard that has the cylinder is the yard that says so." />
      <div className="actions">
        <Button kind="primary" disabled={runner.busy || reason.trim().length === 0 || branch.trim().length === 0}
                onClick={() => {
                  void runner.run((retryKey) => api.command('cylinder.receiveAtBranch', {
                    cylinderId: r.cylinderId, atBranchId: Number(branch), receivedOn: today(), reason: reason.trim(),
                  }, retryKey !== undefined ? { idempotencyKey: retryKey } : {})).then((ok) => { if (ok) onDone(); });
                }}>
          {runner.busy ? 'Recording…' : 'It has arrived here'}
        </Button>
      </div>
    </div>
  );
}

const YARD_COLUMNS: readonly Column<YardRow>[] = [
  { key: 'cyl', head: 'Cylinder', cell: (r) => <><a href={`#/cylinders/${r.cylinderId}`}>{r.internalId}</a> <span className="muted">{r.serialNo}</span></> },
  // THE TWO FACTS, IN TWO COLUMNS. A single "branch" column is how one quietly becomes the other.
  { key: 'yard', head: 'Standing in', cell: (r) => (r.physicalPosition === 'in_transit'
      ? <Pill tone="primary">on a lorry</Pill>
      : r.yardUnknown
        ? <Pill tone="testing">not recorded</Pill>
        : <span>{r.yardBranchCode ?? '—'}</span>) },
  { key: 'home', head: 'Owned by', cell: (r) => <span className="muted">{r.homeBranchCode ?? '—'}</span> },
  { key: 'state', head: 'How it stands', cell: (r) => words(r.legacyState) },
  { key: 'since', head: 'Since', cell: (r) => date(r.since) },
  { key: 'blockers', head: 'Held by', cell: (r) => (r.blockers.length === 0
      ? <span className="muted">nothing</span>
      : <>{r.blockers.map((bk) => <Pill key={bk} tone="danger">{words(bk)}</Pill>)}</>) },
];
